import baseApi from '@store/api/baseApi';

export const costingAgentApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		getCostingByProductId: builder.query({
			query: ({ productId, brandName, singleQty, supplierName, isBuyerRequest, initialCostingId }) => {
				const params = [];
				if (brandName) params.push(`brandName=${brandName}`);
				if (singleQty) params.push(`quantity=${singleQty}`);
				if (supplierName) params.push(`supplierName=${supplierName}`);
				if (isBuyerRequest !== undefined) params.push(`isBuyerRequest=${isBuyerRequest}`);
				if (initialCostingId) params.push(`initialCostingId=${initialCostingId}`);
				const queryString = params.length ? `?${params.join('&')}` : '';
				return `/product/predict-cost/${productId}${queryString}`;
			},
			providesTags: ['Costing']
		}),
		getHistoryByProductId: builder.query({
			query: ({ productId, brandId, initialCostingId }) => {
				const params = [];
				if (brandId) params.push(`brandId=${brandId}`);
				if (initialCostingId) params.push(`initialCostingId=${initialCostingId}`);
				const queryString = params.length ? `?${params.join('&')}` : '';
				return `/product/predict-cost/history/${productId}${queryString}`;
			},
			providesTags: ['History']
		}),
		getPriceRangeByProductId: builder.query({
			query: (productId) => `/product/price-range/${productId}`,
			providesTags: ['PriceRange']
		}),
		getProductInfoByProductId: builder.query({
			query: (productId) => `/product/design-info/${productId}?checkBuyerSelectionStatus=true`,
			providesTags: ['ProductInfo']
		}),
		submitCosting: builder.mutation({
			query: ({ productId, historyId, requestBody, initialCostingId, setProductId }) => ({
				url: `/product/save/predict-cost/${productId}/history/${historyId}${initialCostingId ? `?initialCostingId=${initialCostingId}${setProductId ? `&setProductId=${setProductId}` : ''}` : ''}`,
				method: 'POST',
				body: requestBody
			}),
			invalidatesTags: ['History']
		}),
		handleRecost: builder.mutation({
			query: ({
				productId,
				historyId,
				requestBody,
				brandName,
				singleQty,
				supplierName,
				isBuyerRequest,
				initialCostingId
			}) => {
				const params = [];
				if (brandName) params.push(`brandName=${brandName}`);
				if (singleQty) params.push(`quantity=${singleQty}`);
				if (supplierName) params.push(`supplierName=${supplierName}`);
				if (isBuyerRequest !== undefined) params.push(`isBuyerRequest=${isBuyerRequest}`);
				if (initialCostingId) params.push(`initialCostingId=${initialCostingId}`);
				const queryString = params.length ? `?${params.join('&')}` : '';
				return {
					url: `/product/re-cost/${productId}/history/${historyId}${queryString}`,
					method: 'POST',
					body: requestBody
				};
			},
			// re-costing rewrites the costing itself, not just the history list — but a re-cost that comes back
			// as a new version would only refetch the old id here; the page switches to and fetches the new one
			invalidatesTags: (result, error, { historyId }) =>
				result?.payload?.id != null && String(result.payload.id) !== String(historyId)
					? ['History']
					: ['History', 'HistoryCosting']
		}),
		saveQtyWisePrice: builder.mutation({
			query: ({ costingId, historyId, requestBody }) => {
				return {
					url: historyId
						? `/product/save-qty-wise-costing/${costingId}?versionId=${historyId}`
						: `/product/save-qty-wise-costing/${costingId}`,
					method: 'POST',
					body: requestBody
				};
			},
			invalidatesTags: ['History']
		}),
		getCostingByHistoryId: builder.query({
			query: ({ productId, historyId }) => `/product/predict-cost/${productId}/history/${historyId}`,
			providesTags: ['Costing', 'HistoryCosting']
		}),
		likeUnlikeCosting: builder.query({
			query: ({ productId, historyId, isLiked }) =>
				`/product/predict-cost/${productId}/history/${historyId}/isLiked?isLiked=${isLiked}`,
			providesTags: ['Costing']
		}),
		brandWiseSupplier: builder.query({
			query: ({ brand }) => `/product/get-brand-wise-suppliers?brandName=${brand}`,
			providesTags: ['BrandWiseSupplier']
		}),
		downloadOCS: builder.query({
			query: ({ productId, versionId }) => ({
				url: `/product/predict-cost/${productId}/history/${versionId}/ocs-download`,
				responseHandler: (response) => response.blob()
			}),
			transformResponse: (blob, meta) => ({
				data: blob,
				headers: meta?.response?.headers
			})
		}),
		getSimilarTypesStyle: builder.query({
			query: ({ productId, brandName, quantity, isBuyerRequest, initialCostingId }) =>
				`/costing-agent/get-similar-styles/${productId}?brandName=${encodeURIComponent(brandName)}&quantity=${quantity}&isBuyerRequest=${isBuyerRequest}${
					initialCostingId != null ? `&initialCostingId=${initialCostingId}` : ''
				}`,
			providesTags: ['SimilarTypesStyle']
		}),
		getSimilarTableData: builder.query({
			query: ({ productId, brandName, quantity, isBuyerRequest, initialCostingId }) =>
				`/costing-agent/get-similar-orders/${productId}?brandName=${encodeURIComponent(brandName)}&quantity=${quantity}&isBuyerRequest=${isBuyerRequest}${
					initialCostingId != null ? `&initialCostingId=${initialCostingId}` : ''
				}`,
			providesTags: ['SimilarTableData']
		}),
		getStyleDropdown: builder.query({
			query: ({ brandName }) =>
				`/costing-agent/get-data-set?brandName=${encodeURIComponent(brandName)}`,
			providesTags: ['StyleDropdown']
		}),
		getFindSimilarInformation: builder.query({
			query: ({ productId, initialCostingId }) => ({
				url: `/costing-agent/find-similarity-information?productId=${productId}${
					initialCostingId != null ? `&initialCostingId=${initialCostingId}` : ''
				}`
			})
		}),
		setFobDecision: builder.mutation({
			query: ({ productId, brandName, requestBody, initialCostingId }) => ({
				url: `/costing-agent/find-recommended-fob?productId=${productId}&brandName=${encodeURIComponent(brandName)}${
					initialCostingId != null ? `&initialCostingId=${initialCostingId}` : ''
				}`,
				method: 'POST',
				body: requestBody
			})
		}),
		saveOcsOfferInfo: builder.mutation({
			query: ({ productId, initialCostingId, requestBody }) => ({
				url: `/costing-agent/save-ocs-offer-info?productId=${productId}${initialCostingId !== null ? `&initialCostingId=${initialCostingId}` : ''}`,
				method: 'POST',
				body: requestBody
			})
		}),
		getOcsOfferInfo: builder.mutation({
			query: ({ productId, initialCostingId, requestBody }) => ({
				url: `/costing-agent/get-ocs-offer-info?productId=${productId}${initialCostingId !== null ? `&initialCostingId=${initialCostingId}` : ''}`,
				method: 'POST',
				body: requestBody
			})
		}),
		addScenario: builder.mutation({
			query: ({ productId, initialCostingId, requestBody }) => ({
				url: `/costing-agent/save-new-scenario?productId=${productId}${initialCostingId !== null ? `&initialCostingId=${initialCostingId}` : ''}`,
				method: 'POST',
				body: requestBody
			})
		}),
		scenarioReCost: builder.mutation({
			query: ({ productId, brandName, initialCostingId, singleQty, isBuyerRequest, requestBody }) => ({
				url: `/costing-agent/re-cost?productId=${productId}&brandName=${encodeURIComponent(brandName)}&isBuyerRequest=${isBuyerRequest}${initialCostingId !== null ? `&initialCostingId=${initialCostingId}` : ''}&quantity=${singleQty}`,
				method: 'POST',
				body: requestBody
			})
		})
	})
});

export const {
	useGetCostingByProductIdQuery,
	useGetHistoryByProductIdQuery,
	useSubmitCostingMutation,
	useHandleRecostMutation,
	useSaveQtyWisePriceMutation,
	useGetCostingByHistoryIdQuery,
	useLikeUnlikeCostingQuery,
	useLazyLikeUnlikeCostingQuery,
	useGetPriceRangeByProductIdQuery,
	useGetProductInfoByProductIdQuery,
	useBrandWiseSupplierQuery,
	useLazyDownloadOCSQuery,
	useGetSimilarTypesStyleQuery,
	useGetSimilarTableDataQuery,
	useGetStyleDropdownQuery,
	useGetFindSimilarInformationQuery,
	useSetFobDecisionMutation,
	useSaveOcsOfferInfoMutation,
	useGetOcsOfferInfoMutation,
	useAddScenarioMutation,
	useScenarioReCostMutation
} = costingAgentApi;
